import { Mediator, app, ojs, payload } from "modular-openscriptjs";
import { tool, dom } from "../fotastart";
import {
	approved,
	inform,
	processStart,
	processEnd,
	empty,
	eParse,
} from "../helpers";
import { STRINGS, routes, dc, api } from "../declarations";
import { purCxt } from "../ojs.contexts";

const broker = app("broker");
const $e = app("$e");

export default class Purchase extends Mediator {
	$$purchase = {
		needs: {
			creation: (ed, event) => {
				this.broadcast($e.purchase.needs.form);
			},

			storage: (ed, event) => {
				this.initiateTransaction(ed, event);
			},

			display: (ed, event) => {
				this.getPurchases(ed, event);
			},

			refresh: (ed, event) => {
				this.refreshPurchase(ed, event);
			},
		},

		is: {
			confirming: (ed, event) => {
				this.confirmPurchase(ed, event);
			},
		},

		completed_failed: (ed, event) => {
			processEnd();
			purCxt.additionalAmount.value = 0;
		},
	};

	async getPurchases(ed, event) {
		let data = eParse(ed);

		processStart({ title: "Fetching purchase Information" });
		let res = await api.get(
			`/riders/${routes.purchases}/for-item/${data.message.get("itemId")}`
		);
		processEnd();

		if (!res) return this.send($e.purchase.has.displayError);

		this.send(
			$e.ui.needs.purchaseView,
			payload({ purchases: res.data.purchases })
		);
	}

	async initiateTransaction(ed, event) {
		if (
			!approved(
				event,
				ed,
				"You want to make this purchase? It cannot be undone"
			)
		) {
			return;
		}

		let { message, meta } = eParse(ed);

		if (
			Object.keys(message.get("items", dc.selectedItems.value)).length ==
			0
		) {
			inform(`Please select items to purchase`, STRINGS.WARNING);
			return this.broadcast($e.purchase.has.storageError, ed);
		}

		let type = message.get("type", "till");

		if (
			!this[`${type}Checked`](
				message.get(type),
				message.get("account_number", null)
			)
		) {
			return this.send($e.purchase.has.storageError, ed);
		}

		purCxt.paymentInfo.mpesaType = type;
		purCxt.paymentInfo.mpesaInfo[type].number = message.get(type, "");
		purCxt.paymentInfo.mpesaInfo[type].account = message.get(
			"account_number",
			""
		);

		let information = message.getAll();
		information.number = message.get(type);
		information.method = message.get("method");
		information.payment_type = "debit";

		if (information.method == "offlineMpesa") {
			information.transaction_id = message.get("offline_transaction_id");
		}

		let data = { information };

		data.items = Object.keys(message.get("items", dc.selectedItems.value));
		data.prices = message.get("prices", dc.purchasePrices.value);
		data.package_prices = message.get("package_prices", dc.packagePrices.value);
		data.bought_somewhere_else = message.get("bought_somewhere_else", dc.buyFromSomewhereElse.value);
		data.bought_something_else = message.get("bought_something_else", dc.buyAnotherItem.value);

		let requireLocation = false;
		for (const itemId of data.items) {
			const buySomewhereElse = data.bought_somewhere_else[itemId] === true;
			const buyAnother = data.bought_something_else[itemId] === true;
			if (buySomewhereElse && !buyAnother) {
				requireLocation = true;
				break;
			}
		}

		if (requireLocation && meta.type != "errand") {
			data.latitude = message.get("latitude", dc.latitude);
			data.longitude = message.get("longitude", dc.longitude);

			if (empty(data.latitude)) {
				inform("Please tell us where you're buying from", STRINGS.ERROR, 5000);
				return this.send($e.purchase.has.storageError, ed);
			}
		}

		processStart({ title: "Making Purchase" });

		let res = await api.post(`/riders/${routes.purchases}`, data);

		if (!res) {
			processEnd();
			return this.send($e.purchase.has.storageError, ed);
		}

		processEnd();

		let newEd = eParse(ed);
		newEd.meta.put("paymentId", res.data.response.paymentId);
		newEd.meta.put("purchaseId", res.data.response.metadata.purchase_id);

		newEd.message.put(
			"itemsPurchased",
			message.get("items", tool.deepCopy(dc.selectedItems.value))
		);

		return this.send(
			[$e.purchase.stored, $e.purchase.is.confirming],
			newEd.encode()
		);
	}

	async confirmPurchase(ed, event) {
		setTimeout(
			(() => {
				processStart({ title: "Confirming purchase" });
				this.checkPayment(ed, event);
			}).bind(this),
			5000
		);
	}

	async checkPayment(ed, event) {
		const { meta } = eParse(ed);
		const paymentId = meta.get("paymentId");

		let res = await api.get(`${routes.payments}/${paymentId}`);

		if (!res) return this.broadcast($e.purchase.failed, ed);

		const payment = res.data.payment;
		let status = payment.status;

		if (status in { processing: true, pending: true }) {
			inform("Awaiting purchase completion");

			return setTimeout(
				(() => {
					this.checkPayment(ed, event);
				}).bind(this),
				2000
			);
		}

		if (status in { failed: true, refunded: true }) {
			inform("Purchase Failed/Refunded", STRINGS.ERROR);

			return this.broadcast($e.purchase.failed, ed);
		}

		inform(res.message, STRINGS.SUCCESS);
		return this.broadcast($e.purchase.completed, ed);
	}

	tillChecked(number) {
		if (!/^\d{6,7}$/.test(number)) {
			inform("Till number is invalid", STRINGS.ERROR);
			return false;
		}

		return true;
	}

	phoneChecked(phone) {
		if (!/^\d{9,16}$/.test(phone)) {
			inform("Phone number is invalid", STRINGS.ERROR);
			return false;
		}

		return true;
	}

	transactionChecked(transactionId) {
		if (!/^\w+$/.test(transactionId)) {
			inform("Transaction ID is invalid", STRINGS.ERROR);
			return false;
		}

		return true;
	}

	paybillChecked(paybill, accountNumber) {
		if (!/^\d{6,7}$/.test(paybill)) {
			inform("Paybill number is invalid", STRINGS.ERROR);
			return false;
		}

		if (!/^\w{1,20}$/.test(accountNumber)) {
			inform("Paybill account number is invalid", STRINGS.ERROR);
			return false;
		}

		return true;
	}

	async refreshPurchase(ed, event) {
		let data = eParse(ed);
		const purchaseId = data.message.get("purchaseId");

		let res = await api.post(
			`/riders/${routes.purchases}/${purchaseId}/refresh`
		);

		if (!res) {
			inform(
				"Could not refresh purchase status. Please try again",
				STRINGS.ERROR
			);
			return this.send($e.purchase.has.refreshError, ed);
		}

		inform(res.message, STRINGS.SUCCESS, 4000);
		return this.send($e.purchase.is.refreshing, ed);
	}
}

ojs(Purchase);
